import { POLYGONS, VARS } from "../ThreeJs/Global/variables";
import { sceneManager } from "../ThreeJs/SetUps/SceneManager";
import { sanitizeSceneForReportExport } from "../ThreeJs/Global/functions.js";
import { generateShadeReport } from "./ShadeReport";
import { generateHorizonReport } from "./HorizonReport";
import { generateFacetReport } from "./FacetReport";
import * as THREE from "three";
import { OrbitControls } from "../ThreeJs/Utils/OrbitControls";
import { addASAtoScene, IRRADIANCE_OVERLAY_NAME, renderSceneWithIrradianceOverlay } from "../ThreeJs/Shading/ASADisplay";
import { getOrCreateCache } from "./IrradianceCache";
import { toast } from "../lib/toast";

const getImageElementSrc = (image) => {
  if (!image) return "";
  const src = image.currentSrc || image.src || "";
  return typeof src === "string" ? src : "";
};

const clampTextureChannel = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  if (numeric >= 0 && numeric <= 1) return Math.round(numeric * 255);
  return Math.max(0, Math.min(255, Math.round(numeric)));
};

// Google Maps HD is stored as a THREE.DataTexture rather than an HTML image.
// Convert its raw pixels to an embeddable PNG for ShadeReport.
const serializeTextureRasterToDataUrl = (texture) => {
  const image = texture?.image;
  const width = Number(image?.width);
  const height = Number(image?.height);
  const sourceData = image?.data;
  if (!Number.isFinite(width) || !Number.isFinite(height) || width < 1 || height < 1 || !sourceData) return "";

  const pixelCount = width * height;
  const sourceLength = Number(sourceData.length);
  if (!Number.isFinite(sourceLength) || sourceLength < pixelCount) return "";

  const rawChannelCount = Math.floor(sourceLength / pixelCount);
  const channelCount = rawChannelCount >= 4 ? 4 : rawChannelCount === 3 ? 3 : rawChannelCount === 2 ? 2 : 1;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return "";

  const output = context.createImageData(width, height);
  const shouldFlipY = texture.flipY === true;
  for (let y = 0; y < height; y += 1) {
    const sourceY = shouldFlipY ? height - 1 - y : y;
    for (let x = 0; x < width; x += 1) {
      const sourceIndex = (sourceY * width + x) * channelCount;
      const targetIndex = (y * width + x) * 4;
      output.data[targetIndex] = clampTextureChannel(sourceData[sourceIndex]);
      output.data[targetIndex + 1] = clampTextureChannel(sourceData[sourceIndex + Math.min(1, channelCount - 1)]);
      output.data[targetIndex + 2] = clampTextureChannel(sourceData[sourceIndex + Math.min(2, channelCount - 1)]);
      output.data[targetIndex + 3] = channelCount >= 4 ? clampTextureChannel(sourceData[sourceIndex + 3]) : 255;
    }
  }
  context.putImageData(output, 0, 0);
  return canvas.toDataURL("image/png");
};

const getMapTileSnapshotImage = (mapTile) => {
  const texture = mapTile?.material?.map;
  if (!texture) return "";
  const image = texture.image || texture.source?.data || null;
  const directSrc = getImageElementSrc(image);
  if (directSrc) return directSrc;
  if (typeof image?.toDataURL === "function") return image.toDataURL("image/png");
  try {
    return serializeTextureRasterToDataUrl({ ...texture, image });
  } catch (error) {
    console.warn("Failed to serialize map tile texture for shade report:", error);
    return "";
  }
};

export const downloadHorizonReport = (currentDesign, reportData = {}) => {
  generateHorizonReport(currentDesign, reportData);
};

export const downloadFacetReport = (
  currentDesign,
  { token, designId, shouldDownload = true } = {}
) => {
  return generateFacetReport(currentDesign, {
    auth: { token: token || "" },
    project: {
      id:
        designId ||
        currentDesign?.uuid ||
        currentDesign?.id ||
        currentDesign?.project_id ||
        "",
    },
    shouldDownload,
  });
};

export const downloadShadeReport = (
  currentDesign,
  { token, designId, shouldDownload = true } = {}
) => {
  if (POLYGONS.length === 0) {
    toast.warning("Please draw sections to generate shade report.", {
      key: "shade-report-no-segments",
      duration: 5000,
    });
    return;
  }

  const { scene, camera, controls } = sceneManager.getSceneObjects();
  if (!scene || !camera) {
    toast.warning("The scene is still loading. Please try again in a moment.", {
      key: "shade-report-scene-not-ready",
      duration: 5000,
    });
    return;
  }

  const page =
    shouldDownload !== false ? window.open("", "_blank") : null;
  if (shouldDownload !== false && !page) {
    toast.warning("Popup blocked. Please allow popups for this site to view the report.", {
      key: "shade-report-popup-blocked",
      duration: 7000,
    });
    return;
  }

  let initialSnapshot = "";
  let segmentBaseSnapshot = "";
  let segmentIrradianceSnapshot = "";
  let defaultReportCamera = {
    position: [0, 0, 50],
    target: [0, 0, 0],
    fov: 45,
    up: [0, 0, 1],
  };

  const collectCachedDisplayPoints = () =>
    POLYGONS.flatMap((segment, index) => {
      const cache = getOrCreateCache(segment?.id);
      const points = Array.isArray(cache?.displayPoints) ? cache.displayPoints : [];
      return points.map((point) => ({
        ...point,
        segmentId: segment?.id ?? null,
        segmentIndex: index,
      }));
    });

  const ensureIrradianceOverlaysInScene = (targetScene) => {
    if (!targetScene) return [];

    const existingOverlays = [];
    targetScene.traverse((obj) => {
      if (obj?.name === IRRADIANCE_OVERLAY_NAME) {
        existingOverlays.push(obj);
      }
    });
    if (existingOverlays.length > 0) {
      existingOverlays.forEach((overlay) => {
        overlay.visible = true;
      });
      return existingOverlays;
    }

    const cachedDisplayPoints = collectCachedDisplayPoints();
    if (!cachedDisplayPoints.length) return [];

    const overlays = addASAtoScene(targetScene, cachedDisplayPoints, {
      enableHover: false,
    });
    overlays.forEach((overlay) => {
      overlay.visible = true;
    });
    return overlays;
  };
  try {
    const renderer = sceneManager.renderer;
    if (renderer?.domElement) {
      const safeDataUrl = (quality = 0.9) => {
        try {
          const value = renderer.domElement.toDataURL("image/jpeg", quality);
          return value && value.startsWith("data:image/") ? value : "";
        } catch (err) {
          return "";
        }
      };

      const width = renderer.domElement.width || window.innerWidth || 1920;
      const height = renderer.domElement.height || window.innerHeight || 1080;
      const startupCamera = new THREE.PerspectiveCamera(
        defaultReportCamera.fov,
        Math.max(1, width / Math.max(1, height)),
        camera?.near || 0.1,
        camera?.far || 5000
      );
      startupCamera.up.set(
        defaultReportCamera.up[0],
        defaultReportCamera.up[1],
        defaultReportCamera.up[2]
      );
      startupCamera.position.set(
        defaultReportCamera.position[0],
        defaultReportCamera.position[1],
        defaultReportCamera.position[2]
      );

      let startupControls = null;
      try {
        startupControls = new OrbitControls(startupCamera, renderer.domElement);
        startupControls.mouseButtons = {
          LEFT: null,
          MIDDLE: THREE.MOUSE.PAN,
          RIGHT: THREE.MOUSE.ROTATE,
        };
        startupControls.zoomToCursor = true;
        startupControls.rotateSpeed = 1;
        startupControls.enablePan = true;
        startupControls.maxPolarAngle = Math.PI / 2;
        startupControls.target.set(
          defaultReportCamera.target[0],
          defaultReportCamera.target[1],
          defaultReportCamera.target[2]
        );
        startupControls.update();

        defaultReportCamera = {
          position: startupCamera.position.toArray(),
          target: startupControls.target.toArray(),
          fov: startupCamera.fov || 45,
          up: startupCamera.up.toArray(),
        };
      } finally {
        if (startupControls && typeof startupControls.dispose === "function") {
          startupControls.dispose();
        }
      }

      renderer.render(scene, startupCamera);
      segmentBaseSnapshot = safeDataUrl(0.9) || initialSnapshot;
      initialSnapshot = segmentBaseSnapshot || initialSnapshot;

      const exportSceneForIrradiance = sanitizeSceneForReportExport(THREE, scene);
      ensureIrradianceOverlaysInScene(exportSceneForIrradiance);
      renderSceneWithIrradianceOverlay(renderer, exportSceneForIrradiance, startupCamera);
      segmentIrradianceSnapshot =
        safeDataUrl(0.9) || segmentBaseSnapshot || initialSnapshot;

      renderer.render(scene, camera);

      if (page) {
        requestAnimationFrame(() => {
        try {
          const snap = segmentBaseSnapshot || initialSnapshot;
          if (snap) {
            page.postMessage(
              { type: "SHADE_REPORT_SET_SNAPSHOT", snapshot: snap },
              "*"
            );
          }
        } catch (e) {}
        });
      }
    }
  } catch (e) {
    console.warn("Initial snapshot failed:", e);
  }

  const cameraPosition = camera?.position
    ? camera.position.toArray()
    : [0, 0, 50];
  const cameraTarget =
    controls && controls.target && controls.target.toArray
      ? controls.target.toArray()
      : [0, 0, 0];
  const mapTileImage = getMapTileSnapshotImage(VARS?.mapTile);

  const reportPayload = {
    snapshot: initialSnapshot,
    segmentBaseSnapshot: segmentBaseSnapshot || initialSnapshot,
    segmentIrradianceSnapshot:
      segmentIrradianceSnapshot || segmentBaseSnapshot || initialSnapshot,
    segmentBaseCamera: defaultReportCamera,
    mapTileImage,
    camera: {
      position: cameraPosition,
      target: cameraTarget,
      fov: camera.fov || 45,
    },
    meta: {
      polygonsCount: POLYGONS.length,
    },
    auth: {
      token: token || "",
    },
    project: {
      id:
        designId ||
        currentDesign?.uuid ||
        currentDesign?.id ||
        currentDesign?.project_id ||
        "",
    },
    __reportWindowRef: page,
    shouldDownload,
  };

  const onPopupReady = (event) => {
    if (event?.data?.type !== "SHADE_REPORT_READY") return;
    try {
      const snap = segmentBaseSnapshot || initialSnapshot;
      if (snap && snap.startsWith("data:image/")) {
        page.postMessage(
          { type: "SHADE_REPORT_SET_SNAPSHOT", snapshot: snap },
          "*"
        );
      }
    } catch (e) {}
    window.removeEventListener("message", onPopupReady);
  };
  if (page) {
    window.addEventListener("message", onPopupReady);
  }

  const onRequestScene = (event) => {
    if (event?.data?.type !== "REQUEST_SHADE_REPORT_SCENE") return;

    try {
      const exportScene = sanitizeSceneForReportExport(THREE, scene);
      ensureIrradianceOverlaysInScene(exportScene);
      const sceneJSON = exportScene.toJSON();

      event.source?.postMessage(
        {
          type: "SHADE_REPORT_SCENE_DATA",
          payload: {
            scene: sceneJSON,
            camera: defaultReportCamera,
          },
        },
        "*"
      );
    } catch (err) {
      console.error("Failed to export scene for shade report:", err);
      event.source?.postMessage(
        {
          type: "SHADE_REPORT_SCENE_ERROR",
          error: String(err?.message || err),
        },
        "*"
      );
    }
  };

  if (page) {
    window.addEventListener("message", onRequestScene);
  }

  generateShadeReport(currentDesign, reportPayload)
    .catch((e) => console.error("generateShadeReport failed:", e))
    .finally(() => {
      if (page) {
        setTimeout(
          () => window.removeEventListener("message", onRequestScene),
          120000
        );
      }
    });
};
